import { ReactComponent as ChevronDownIcon } from "../../stack/environment/icons/chevron-down.svg";

import InfoIcon from "@mui/icons-material/Info";
import { Alert, Box, FormControl, MenuItem, Typography } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { agentConnectorImagesKeys } from "../../../api/queryKeys";
import ContainedButton from "../../../components/buttons/ContainedButton";
import CustomToolTip from "../../../components/CustomTooltip";
import { StyledSelect } from "../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import SvgToIcon from "../../../icons/SvgToIcon";
import { updatedXTimeAgo } from "../../../utils/timestamp";
import { icons } from "../../stack/environment/icons/iconExport";
import DigestWithCopy from "./DigestWithCopy";
import OnPremCollapseSection from "./OnPremCollapseSection";

export default function ContainerImageSection() {
  const colors = useColors();
  const { getAgentConnectorImages, downloadAgentConnectorImage, trackEvent } =
    useAPI();

  const [imageVersion, setImageVersion] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [showAlertLoading, setShowAlertLoading] = useState(false);

  const { data: imageArtifacts = [], isSuccess } = useQuery({
    queryKey: agentConnectorImagesKeys.all,
    queryFn: async () => {
      const { data } = await getAgentConnectorImages();
      return Array.isArray(data) ? data : [];
    },
  });

  useEffect(() => {
    if (
      isSuccess &&
      Array.isArray(imageArtifacts) &&
      imageArtifacts.length > 0
    ) {
      setImageVersion(imageArtifacts[0].version);
    }
  }, [isSuccess, imageArtifacts]);

  const latestImageVersion = useMemo(
    () => imageArtifacts[0]?.version || "",
    [imageArtifacts]
  );
  const effectiveImageVersion = useMemo(
    () => imageVersion || latestImageVersion,
    [imageVersion, latestImageVersion]
  );
  const selectedImage = useMemo(() => {
    return (
      imageArtifacts.find((a) => a.version === effectiveImageVersion) ||
      imageArtifacts[0] ||
      {}
    );
  }, [imageArtifacts, effectiveImageVersion]);

  const handleDownload = useCallback(async () => {
    trackEvent(
      `User clicked on download agent connector image button for version ${effectiveImageVersion}.`
    );

    try {
      setIsDownloading(true);
      const imageName = selectedImage.imageName || "";
      const version = effectiveImageVersion || "";
      const fileTypeLower = (selectedImage.fileType || "").toLowerCase();
      if (!imageName || !version || !fileTypeLower) {
        Toast.error("Missing image details to download.");
        return;
      }
      const filename = `${selectedImage.vendor}-image-${imageName}-${version}.${fileTypeLower}`;
      const { data } = await downloadAgentConnectorImage(
        imageName,
        version,
        fileTypeLower
      );
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      Toast.success("Container Image downloaded successfully.");
    } catch (e) {
      Toast.error("Failed to download Container Image.");
    } finally {
      setIsDownloading(false);
    }
  }, [
    selectedImage,
    effectiveImageVersion,
    downloadAgentConnectorImage,
    trackEvent,
  ]);

  const handleVersionChange = useCallback((e) => {
    setImageVersion(e.target.value);
    setShowAlertLoading(true);
    // TODO: currently, we sleep for half a second, in the future we should run a scan on the binary https://linear.app/breeze-security/issue/DEV-4924/use-free-image-scanner-to-scan-our-image
    setTimeout(() => setShowAlertLoading(false), 500);
  }, []);

  return (
    <OnPremCollapseSection
      title="Download Container Image"
      defaultCollapsed={false}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 4 }}>
        <Typography variant="body1">
          Contains the Breeze connector runtime and environment image used for
          your on-prem deployment.
        </Typography>

        <FormControl size="small" sx={{ width: 360 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              Recent versions
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <StyledSelect
                labelId={"config-version-label"}
                displayEmpty
                value={effectiveImageVersion}
                IconComponent={ChevronDownIcon}
                onChange={handleVersionChange}
              >
                {imageArtifacts.map((a) => (
                  <MenuItem key={a.version} value={a.version}>
                    <Typography variant="caption">
                      {`${a.vendor}/${a.imageName}:${a.version}`}
                      {a.version === latestImageVersion ? " (latest)" : ""}
                    </Typography>
                  </MenuItem>
                ))}
              </StyledSelect>
              {!isSuccess ? (
                <FormHelperText sx={{ color: colors.helper_text }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LoadingIcon size={12} />
                    Loading image info...
                  </Box>
                </FormHelperText>
              ) : imageVersion ? (
                <FormHelperText sx={{ color: colors.helper_text }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoIcon sx={{ width: "12px", height: "12px" }} />
                    {`${selectedImage.fileType || "Bin"} · ${
                      selectedImage.size || "0 MB"
                    } · ${
                      selectedImage.lastUpdated
                        ? updatedXTimeAgo(selectedImage.lastUpdated)
                        : "N/A"
                    }`}
                  </Box>
                </FormHelperText>
              ) : null}
            </Box>
          </Box>
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              Digest (md5)
            </Typography>
            <DigestWithCopy digest={selectedImage.digest || ""} />
          </Box>
          {showAlertLoading ? (
            <Box
              sx={{
                alignSelf: "flex-end",
                mb: 1,
                height: "40px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <LoadingIcon size={18} />
            </Box>
          ) : isSuccess && !!imageVersion ? (
            <Box sx={{ alignSelf: "flex-end", mb: 1 }}>
              <CustomToolTip
                title={
                  <Typography
                    variant="caption"
                    sx={{ color: colors.common_black }}
                  >
                    {selectedImage.scanSummary || ""}
                  </Typography>
                }
                placement="top"
              >
                <Alert
                  severity="success"
                  icon={false}
                  sx={{
                    display: "flex",
                    height: "40px",
                    padding: "8px 8px 8px 4px",
                    alignItems: "center",
                    border: "0.5px solid",
                    borderRadius: "6px",
                    borderColor: colors.alert_success_border,
                    backgroundColor: colors.alert_success_bg,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <SvgToIcon IconLogo={icons.severityLowIcon} width="18px" />
                    <Typography
                      variant="overline"
                      sx={{ color: colors.text_primary }}
                    >
                      Security Scan: Passed
                    </Typography>
                  </Box>
                </Alert>
              </CustomToolTip>
            </Box>
          ) : null}
        </Box>

        <ContainedButton
          color="primary"
          size="small"
          onClick={handleDownload}
          disabled={isDownloading || !isSuccess || !effectiveImageVersion}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {isDownloading ? (
              <LoadingIcon size={14} />
            ) : (
              <SvgToIcon IconLogo={icons.ActionDownloadIcon} width="18px" />
            )}
            <Typography variant="overline">
              {isDownloading ? "Downloading..." : "Download Image"}
            </Typography>
          </Box>
        </ContainedButton>
      </Box>
    </OnPremCollapseSection>
  );
}
